import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ComponentType,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, type LinkProps } from "react-router-dom";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconEye,
  IconEyeOff,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import { ApiError } from "../lib/api";
import { useI18n } from "../i18n";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type IconType = ComponentType<{ size?: number; className?: string; stroke?: number }>;

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type Variant = "primary" | "secondary" | "ghost" | "danger" | "subtle";
type Size = "sm" | "md" | "lg";

const BTN_BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition active:scale-[.98] disabled:pointer-events-none disabled:opacity-50 select-none";
const BTN_VARIANT: Record<Variant, string> = {
  primary: "bg-primary text-white shadow-sm hover:bg-primary-hover",
  secondary: "bg-surface text-ink ring-1 ring-inset ring-line-strong hover:bg-sunken",
  ghost: "bg-transparent text-brand hover:bg-primary/10",
  danger: "bg-danger text-white hover:opacity-90 dark:text-canvas",
  subtle: "bg-sunken text-ink hover:bg-line",
};
const BTN_SIZE: Record<Size, string> = {
  sm: "min-h-[36px] px-3 text-sm",
  md: "min-h-[44px] px-4 text-sm",
  lg: "min-h-[52px] px-5 text-base",
};

export const buttonCls = (variant: Variant = "primary", size: Size = "md", block?: boolean, className?: string) =>
  cx(BTN_BASE, BTN_VARIANT[variant], BTN_SIZE[size], block && "w-full", className);

export function Button({
  variant = "primary",
  size = "md",
  block,
  loading,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; block?: boolean; loading?: boolean }) {
  return (
    <button {...rest} type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonCls(variant, size, block, className)}>
      {loading && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />}
      {children}
    </button>
  );
}

export function ButtonLink({ variant = "primary", size = "md", block, className, ...rest }: LinkProps & { variant?: Variant; size?: Size; block?: boolean }) {
  return <Link {...rest} className={buttonCls(variant, size, block, className)} />;
}

export function IconButton({ label, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type="button" aria-label={label} title={label} {...rest} className={cx("inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition hover:bg-sunken active:scale-95", className)}>
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

export function Field({ label, hint, error, children, htmlFor, optional }: { label: string; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor?: string; optional?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
        {optional && <span className="ml-1 font-normal text-subtle">({t("optional")})</span>}
      </label>
      {children}
      {error ? (
        <p className="flex items-start gap-1 text-xs text-danger" role="alert">
          <IconAlertCircle size={14} className="mt-px shrink-0" />
          {error}
        </p>
      ) : (
        hint && <p className="text-xs text-subtle">{hint}</p>
      )}
    </div>
  );
}

export const inputCls = (invalid?: boolean) =>
  cx(
    "min-h-[48px] w-full rounded-xl border bg-surface px-3.5 py-2.5 text-base text-ink outline-none transition placeholder:text-subtle/70",
    "focus:border-primary focus:ring-4 focus:ring-primary/15 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-muted",
    invalid ? "border-danger" : "border-line-strong"
  );

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode; error?: string | null; optional?: boolean; leading?: ReactNode; trailing?: ReactNode };

export function Input({ label, hint, error, optional, leading, trailing, className, ...rest }: InputProps) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
      <div className="relative flex items-center">
        {leading && <span className="pointer-events-none absolute left-3.5 flex items-center text-muted">{leading}</span>}
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          {...rest}
          className={cx(inputCls(Boolean(error)), Boolean(leading) && "pl-14", Boolean(trailing) && "pr-12", className)}
        />
        {trailing && <span className="absolute right-1.5 flex items-center">{trailing}</span>}
      </div>
    </Field>
  );
}

export function PasswordInput(props: Omit<InputProps, "type" | "trailing">) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  return (
    <Input
      {...props}
      type={show ? "text" : "password"}
      trailing={
        <IconButton label={show ? t("hidePassword") : t("showPassword")} onClick={() => setShow(!show)} className="h-9 w-9 text-muted">
          {show ? <IconEyeOff size={18} /> : <IconEye size={18} />}
        </IconButton>
      }
    />
  );
}

/** Tanzanian mobile: "+255" shown, the agent types the rest (0712…, 712… and +255… all work). */
export function PhoneInput(props: Omit<InputProps, "type" | "leading" | "inputMode">) {
  return <Input {...props} type="tel" inputMode="tel" leading={<span className="text-base font-medium text-muted">+255</span>} placeholder={props.placeholder ?? "712 345 678"} />;
}

/** One-time SMS code: big, spaced digits. */
export function CodeInput(props: Omit<InputProps, "inputMode" | "autoComplete">) {
  return (
    <Input
      {...props}
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={props.maxLength ?? 8}
      onChange={(e) => {
        e.target.value = e.target.value.replace(/\D/g, "");
        props.onChange?.(e);
      }}
      className="text-center font-mono text-xl tracking-[0.5em]"
    />
  );
}

export function Select({
  label,
  hint,
  error,
  optional,
  options,
  placeholder,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hint?: ReactNode; error?: string | null; optional?: boolean; options: { value: string; label: string }[]; placeholder?: string }) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} htmlFor={id} optional={optional}>
      <div className="relative">
        <select id={id} {...rest} className={cx(inputCls(Boolean(error)), "appearance-none pr-10")}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <IconChevronDown size={18} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted" />
      </div>
    </Field>
  );
}

export function Checkbox({ checked, onChange, children, disabled }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; disabled?: boolean }) {
  return (
    <label className={cx("flex cursor-pointer items-start gap-3 rounded-xl p-1 text-sm leading-snug", disabled && "cursor-not-allowed opacity-60")}>
      <span className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="peer absolute inset-0 cursor-pointer appearance-none rounded-md border-2 border-line-strong bg-surface transition checked:border-primary checked:bg-primary" />
        <IconCheck size={14} stroke={3} className="pointer-events-none relative text-white opacity-0 peer-checked:opacity-100" />
      </span>
      <span className="text-ink">{children}</span>
    </label>
  );
}

/** Two or three mutually exclusive choices, as one pill row. */
export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; icon?: IconType }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex rounded-xl bg-sunken p-1">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cx("flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-medium transition", on ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink")}
          >
            {o.icon && <o.icon size={16} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Horizontal filter chips that scroll on phones. */
export function Chips<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; count?: number }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cx(
              "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium ring-1 ring-inset transition",
              on ? "bg-ink text-canvas ring-ink" : "bg-surface text-ink ring-line-strong hover:bg-sunken"
            )}
          >
            {o.label}
            {o.count != null && <span className={cx("tabular rounded-full px-1.5 text-xs", on ? "bg-canvas/20" : "bg-sunken text-muted")}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({ children, className, as: As = "div" }: { children: ReactNode; className?: string; as?: "div" | "section" | "li" }) {
  return <As className={cx("rounded-2xl border border-line bg-surface p-4 shadow-card sm:p-5", className)}>{children}</As>;
}

export function CardHeader({ title, subtitle, action, icon: Icon }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; icon?: IconType }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-brand">
            <Icon size={18} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="font-semibold text-ink">{title}</h2>
          {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

const ALERT: Record<string, { cls: string; icon: IconType }> = {
  info: { cls: "bg-info-soft text-info", icon: IconInfoCircle },
  success: { cls: "bg-success-soft text-success", icon: IconCircleCheck },
  warning: { cls: "bg-warning-soft text-warning", icon: IconAlertTriangle },
  error: { cls: "bg-danger-soft text-danger", icon: IconAlertCircle },
};

export function Alert({ tone = "info", title, children, action }: { tone?: "info" | "success" | "warning" | "error"; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const { cls, icon: Icon } = ALERT[tone];
  return (
    <div className={cx("flex gap-3 rounded-xl p-3.5 text-sm", cls)} role={tone === "error" ? "alert" : "status"}>
      <Icon size={20} className="mt-px shrink-0" />
      <div className="min-w-0 flex-1 text-ink">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? "mt-1 text-ink/85" : ""}>{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

const BADGE: Record<string, string> = {
  green: "bg-success-soft text-success",
  yellow: "bg-warning-soft text-warning",
  red: "bg-danger-soft text-danger",
  gray: "bg-sunken text-muted",
  blue: "bg-info-soft text-info",
};
const TONE: Record<string, keyof typeof BADGE> = {
  active: "green",
  completed: "green",
  paid: "green",
  approved: "green",
  inProgress: "blue",
  pending: "yellow",
  locked: "gray",
  underReview: "yellow",
  documentsPending: "yellow",
  documentsExpired: "yellow",
  rejected: "red",
  suspended: "red",
  deleted: "red",
  reversed: "red",
  expired: "gray",
  cancelled: "gray",
  closed: "gray",
  draft: "gray",
};
export const toneOf = (status?: string) => TONE[status ?? ""] ?? "gray";

export function Badge({ status, tone, children }: { status?: string; tone?: keyof typeof BADGE; children: ReactNode }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium", BADGE[tone ?? toneOf(status)])}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {children}
    </span>
  );
}

export function Progress({ value, label, tone = "primary" }: { value: number; label?: string; tone?: "primary" | "success" }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-sunken" role="progressbar" aria-label={label} aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cx("h-full rounded-full transition-[width] duration-500", tone === "success" ? "bg-success" : "bg-primary")} style={{ width: `${v}%` }} />
    </div>
  );
}

export function Avatar({ name, src, size = 44, className }: { name: string; src?: string | null; size?: number; className?: string }) {
  const [failed, setFailed] = useState(false);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  const ok = src && /^(https?:|blob:|data:)/.test(src) && !failed;
  return (
    <span className={cx("inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-soft font-semibold text-brand", className)} style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {ok ? <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" onError={() => setFailed(true)} /> : initials || "?"}
    </span>
  );
}

/** A tappable row: icon, text, chevron. Used for menus and settings. */
export function ListRow({
  to,
  onClick,
  icon: Icon,
  title,
  subtitle,
  trailing,
  danger,
  external,
}: {
  to?: string;
  onClick?: () => void;
  icon?: IconType;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  danger?: boolean;
  external?: boolean;
}) {
  const inner = (
    <>
      {Icon && (
        <span className={cx("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", danger ? "bg-danger-soft text-danger" : "bg-sunken text-ink")}>
          <Icon size={20} />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={cx("block font-medium", danger ? "text-danger" : "text-ink")}>{title}</span>
        {subtitle && <span className="mt-0.5 block truncate text-sm text-muted">{subtitle}</span>}
      </span>
      {trailing ?? ((to || onClick) && <IconChevronRight size={18} className="shrink-0 text-subtle" />)}
    </>
  );
  const cls = "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-sunken active:bg-sunken";
  if (to && external)
    return (
      <a href={to} className={cls} target={to.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
        {inner}
      </a>
    );
  if (to)
    return (
      <Link to={to} className={cls}>
        {inner}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" onClick={onClick} className={cls}>
        {inner}
      </button>
    );
  return <div className={cx(cls, "hover:bg-transparent")}>{inner}</div>;
}

export function Stat({ label, value, hint, icon: Icon, to, accent }: { label: string; value: ReactNode; hint?: string; icon?: IconType; to?: string; accent?: boolean }) {
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-muted">{label}</p>
        {Icon && <Icon size={18} className={accent ? "text-brand" : "text-subtle"} />}
      </div>
      <p className="tabular mt-2 text-xl font-semibold tracking-tight text-ink sm:text-2xl">{value}</p>
      {hint && <p className="mt-1 text-xs leading-snug text-subtle">{hint}</p>}
    </>
  );
  const cls = "block rounded-2xl border border-line bg-surface p-4 shadow-card transition";
  return to ? (
    <Link to={to} className={cx(cls, "hover:border-line-strong active:scale-[.99]")}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

// ---------------------------------------------------------------------------
// Loading, empty and error states
// ---------------------------------------------------------------------------

export function Spinner({ label, inline }: { label?: string; inline?: boolean }) {
  const { t } = useI18n();
  return (
    <div className={cx("flex items-center justify-center gap-2 text-sm text-muted", !inline && "py-12")} role="status">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden />
      {label ?? t("loading")}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span className={cx("relative block overflow-hidden rounded-lg bg-sunken", className)} aria-hidden>
      <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-surface/60 to-transparent" />
    </span>
  );
}

/** Placeholder rows while a list loads. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-3" role="status" aria-label={t("loading")}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl border border-line bg-surface p-4">
          <Skeleton className="h-11 w-11 !rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ image, icon: Icon, title, body, action }: { image?: string; icon?: IconType; title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-line-strong bg-surface px-6 py-10 text-center">
      {image ? (
        <img src={image} alt="" className="mb-4 h-28 w-auto dark:opacity-90" />
      ) : (
        Icon && (
          <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sunken text-muted">
            <Icon size={28} />
          </span>
        )
      )}
      <p className="font-semibold text-ink">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-muted">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** The bilingual message from an API error, in the current language. */
export function useErrorText() {
  const { t, pick } = useI18n();
  return (e: unknown) => (e instanceof ApiError ? pick(e.messages) : e ? t("somethingWrong") : null);
}

export function ErrorAlert({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();
  const text = useErrorText()(error);
  if (!text) return null;
  // Server-side failures: point at support (the message carries the reference).
  const needsSupport = error instanceof ApiError ? error.status >= 500 && error.code !== "unavailable" : true;
  const link = "text-sm font-semibold text-danger underline underline-offset-2";
  return (
    <Alert
      tone="error"
      action={
        (onRetry || needsSupport) && (
          <div className="flex flex-wrap gap-4">
            {onRetry && (
              <button type="button" className={link} onClick={onRetry}>
                {t("retry")}
              </button>
            )}
            {needsSupport && (
              <Link to="/help" className={link}>
                {t("contactSupport")}
              </Link>
            )}
          </div>
        )
      }
    >
      {text}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Page chrome
// ---------------------------------------------------------------------------

export function PageTitle({ children, subtitle, action, back }: { children: ReactNode; subtitle?: ReactNode; action?: ReactNode; back?: string | true }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="mb-5">
      {back && (
        <button
          type="button"
          onClick={() => (back === true || window.history.length > 1 ? navigate(-1) : navigate(back))}
          className="-ml-2 mb-2 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-muted hover:text-ink"
        >
          <IconArrowLeft size={18} />
          {t("back")}
        </button>
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-ink sm:text-[28px]">{children}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 mt-2 flex items-center justify-between gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-subtle">{children}</h2>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sheet: bottom sheet on phones, centred dialog on larger screens
// ---------------------------------------------------------------------------

export function Sheet({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode }) {
  const { t } = useI18n();
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Callers pass inline handlers; reading through a ref keeps the effect from
  // re-running (and stealing focus) on every keystroke inside the sheet.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the first field, else the panel.
    requestAnimationFrame(() => {
      const first = panel.current?.querySelector<HTMLElement>("input, select, textarea");
      (first ?? panel.current)?.focus();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
      if (e.key === "Tab" && panel.current) {
        const items = panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])');
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 animate-fade-in bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative flex max-h-[92dvh] w-full animate-slide-up flex-col rounded-t-3xl bg-surface shadow-raised outline-none sm:max-w-lg sm:animate-pop-in sm:rounded-3xl"
      >
        <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-line-strong sm:hidden" aria-hidden />
        <div className="flex items-center justify-between gap-3 px-5 pb-2 pt-3 sm:pt-5">
          <h2 id={titleId} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          <IconButton label={t("close")} onClick={onClose} className="-mr-2 text-muted">
            <IconX size={20} />
          </IconButton>
        </div>
        <div className="overflow-y-auto overscroll-contain px-5 pb-5">{children}</div>
        {footer && <div className="pb-safe border-t border-line px-5 py-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

type Toast = { id: number; tone: "success" | "error" | "info"; text: string };
const ToastCtx = createContext<(text: string, tone?: Toast["tone"]) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x.slice(-2), { id, tone, text }]);
    setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 bottom-[calc(84px+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6" aria-live="polite">
          {items.map((i) => {
            const Icon = i.tone === "error" ? IconAlertCircle : i.tone === "info" ? IconInfoCircle : IconCircleCheck;
            return (
              <div key={i.id} className="pointer-events-auto flex max-w-md animate-pop-in items-center gap-2.5 rounded-2xl bg-ink px-4 py-3 text-sm font-medium text-canvas shadow-raised">
                <Icon size={18} className={i.tone === "error" ? "text-[#FF8A80]" : i.tone === "info" ? "text-[#8AB8FF]" : "text-[#6EE7A0]"} />
                {i.text}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

// ---------------------------------------------------------------------------
// Data hooks
// ---------------------------------------------------------------------------

/** Load something on mount (and on deps change), with reload. Keeps the last data while reloading. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  // Callers pass the deps that `fn` closes over, like useEffect.
  const load = useCallback(fn, deps);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await load());
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [load]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload, setData };
}

/** Run an action with loading + error state. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      setLoading(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [fn]
  );
  return { run, loading, error, setError };
}

/** Seconds left before an SMS code can be re-sent. */
export function useCooldown(seconds = 60) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);
  return { left, start: () => setLeft(seconds) };
}

/** Copy text, with a "copied" flag for two seconds. */
export function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older browsers / blocked clipboard: fall back to a hidden field.
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
      } finally {
        el.remove();
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);
  return { copied, copy };
}
